import { EDITOR_EXPORT_STARTED, safeGet, safeSet } from '@repo/ui';

/**
 * Link back to the dashboard tab that opened this editor.
 *
 * The dashboard runs on a different subdomain, so it hands its origin off in the
 * URL hash (`#at=…&origin=…`) exactly like the access token. We keep it in
 * sessionStorage (tab-scoped) because the hash is stripped right after startup.
 */
const OPENER_ORIGIN_KEY = 'editor_opener_origin';

/** Only ever post back to a real https origin (or a local dev server). */
function isSafeOrigin(value: string): boolean {
  try {
    const url = new URL(value);
    if (url.origin !== value) return false;
    return (
      url.protocol === 'https:' || url.hostname === 'localhost' || url.hostname === '127.0.0.1'
    );
  } catch {
    return false;
  }
}

/**
 * Capture the dashboard origin handed off in the URL hash. Must run BEFORE
 * `captureTokenFromHash`, which clears the hash.
 */
export function captureOpenerFromHash(): void {
  if (typeof window === 'undefined' || !window.location.hash) return;
  const match = /[#&]origin=([^&]+)/.exec(window.location.hash);
  if (!match?.[1]) return;
  const origin = decodeURIComponent(match[1]);
  if (!isSafeOrigin(origin)) return;
  safeSet(OPENER_ORIGIN_KEY, origin, 'session');
}

/**
 * Tell the dashboard tab that a render just started, so it can switch to the
 * '렌더 완료' tab and show the in-progress card. No-op when the editor was
 * opened directly (no opener) — the caller still closes the tab.
 */
export function notifyExportStarted(projectId: string): void {
  const origin = safeGet(OPENER_ORIGIN_KEY, 'session');
  if (!origin || !window.opener) return;
  try {
    window.opener.postMessage({ type: EDITOR_EXPORT_STARTED, projectId }, origin);
  } catch {
    // Dashboard tab already gone / cross-origin lockdown — nothing to do.
  }
}
